import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, GripHorizontal, X } from "lucide-react";
import IagoAvatar from "./IagoAvatar";
import { useStore } from "../../stores/store";

interface Step {
  target?: string;
  title: string;
  body: string;
  cta?: string;
  state?: "idle" | "greeting" | "thinking";
  /** Mostra um input de texto neste step (usado para perguntar o nome). */
  nameInput?: boolean;
}

const STEPS: Step[] = [
  {
    title: "Prazer em conhecer você.",
    body:
      "Sou o IAGO, o copiloto de organização do on_board. Estou aqui para te ajudar a transformar ideias soltas em tarefas organizadas, usando voz e inteligência artificial. Antes de começar, como posso te chamar?",
    state: "greeting",
    nameInput: true,
    cta: "Próximo",
  },
  {
    title: "O que faz o on_board diferente",
    body:
      "Diferente de um kanban comum, aqui você **cria tarefas por voz** (fala o que tem na cabeça e eu transcrevo, organizo e estruturo). Pode **conversar comigo no chat** para planejar, priorizar e quebrar tarefas grandes. Cria **categorias** para separar projetos, contextos e vida pessoal. E o **rescore automático** reordena suas tarefas por urgência com inteligência artificial. Tudo é refinado por IA, mas as decisões são sempre suas. Seus dados ficam criptografados e isolados.",
  },
  {
    target: "[data-tour='board']",
    title: "As raias do seu fluxo",
    body:
      "**Backlog** é a caixa de entrada. Tudo chega aqui ainda cru. **Planejado** guarda o que você já refinou e pretende fazer em breve. **Hoje** é o que vai atacar neste dia. **Em Progresso** é o que está fazendo agora. **Bloqueado** para o que travou por algum motivo. **Concluído** para o que terminou. Cada raia tem um propósito claro, e mover tarefas entre elas é o coração da sua organização.",
  },
  {
    target: "[data-tour='settings']",
    title: "Categorias e configurações",
    body:
      "No ícone de **engrenagem** (⚙) você encontra as **Configurações**. É lá que você cria suas categorias: projetos, contextos de trabalho, vida pessoal, estudos, o que fizer sentido. Eu uso elas para organizar seus cards e sugerir coisas. Categoria que você não criou, eu não invento. A chave Groq e outras preferências também ficam por ali.",
  },
  {
    title: "Sua rotina com o on_board",
    body:
      "Toda tarefa entra crua no **Backlog**, refinada pela IA. **No começo da semana**, revise o Backlog, refine o que faz sentido e mova para **Planejado** (inclua extras para ir desafogando). **Na noite anterior ou pela manhã**, puxe poucas tarefas de Planejado para **Hoje**. Use o chat comigo para ajudar a decidir o quê e em que ordem. **Ao iniciar**, mova para **Em Progresso**. Tente manter um card por vez aqui. Faz diferença. Se algo bloquear, jogue em **Bloqueado** sem culpa. Ao terminar, **Concluído**. Se não deu tempo no dia, continue em Hoje ou volte para Planejado.",
  },
  {
    title: "Quase lá. Sua chave Groq.",
    body:
      "Para a parte de voz e chat funcionar, preciso de uma chave gratuita da Groq no seu perfil. Leva cerca de um minuto para configurar e fica guardada criptografada só para você. Eu te mostro o caminho agora.",
    cta: "Configurar agora",
  },
  {
    target: "[data-tour='record']",
    title: "Tudo pronto. Crie sua primeira tarefa!",
    body:
      "Agora é com você. Clique em **Nova tarefa**, fale ou digite o que tem em mente e eu cuido do resto. Pode ser algo simples como \"comprar café\" ou algo maior como \"planejar apresentação de sexta\". O importante é começar.",
    state: "greeting",
    cta: "Vamos lá",
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function useTargetRect(selector: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);
  useLayoutEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    function measure() {
      const el = document.querySelector(selector!);
      if (!el) {
        console.warn("[IagoTour] elemento não encontrado:", selector);
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      console.log("[IagoTour] medido:", selector, { top: r.top, left: r.left, w: r.width, h: r.height });
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    const timeout = setTimeout(measure, 80);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(document.documentElement);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(timeout);
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [selector]);
  return rect;
}

interface Props {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const CARD_W = 400;
const CARD_H_FALLBACK = 280;
const TOP_OVERHANG = 58;
const SAFE = 24;
const GAP = 20;

const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(min, v), max);

interface Spotlight {
  x: number;
  y: number;
  width: number;
  height: number;
}

function autoPlace(
  s: Spotlight | null,
  vp: { w: number; h: number },
  cardH: number,
): { top: number; left: number } {
  if (!s) {
    return {
      top: Math.max(TOP_OVERHANG + SAFE, vp.h / 2 - cardH / 2),
      left: vp.w / 2 - CARD_W / 2,
    };
  }

  const occupiesMost =
    s.height > vp.h * 0.55 || s.width > vp.w * 0.7;

  if (occupiesMost) {
    return {
      top: vp.h - cardH - SAFE,
      left: vp.w - CARD_W - SAFE,
    };
  }

  const below = s.y + s.height + GAP;
  const above = s.y - cardH - GAP;
  const right = s.x + s.width + GAP;
  const left = s.x - CARD_W - GAP;

  const fitsBelow = below + cardH + SAFE <= vp.h;
  const fitsAbove = above >= TOP_OVERHANG + SAFE;
  const fitsRight = right + CARD_W + SAFE <= vp.w;
  const fitsLeft = left >= SAFE;

  const sideTop = Math.max(
    TOP_OVERHANG + SAFE,
    Math.min(s.y + s.height / 2 - cardH / 2, vp.h - cardH - SAFE),
  );
  const horizCentered = Math.max(
    SAFE,
    Math.min(s.x + s.width / 2 - CARD_W / 2, vp.w - CARD_W - SAFE),
  );

  if (fitsBelow) return { top: below, left: horizCentered };
  if (fitsAbove) return { top: above, left: horizCentered };
  if (fitsRight) return { top: sideTop, left: right };
  if (fitsLeft) return { top: sideTop, left };

  return {
    top: vp.h - cardH - SAFE,
    left: vp.w - CARD_W - SAFE,
  };
}

export default function IagoTour({ open, onComplete, onSkip }: Props) {
  const [idx, setIdx] = useState(0);
  const [vp, setVp] = useState({ w: 1024, h: 768 });
  const [cardH, setCardH] = useState(CARD_H_FALLBACK);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [nickname, setNickname] = useState("");
  const cardRef = useRef<HTMLDivElement | null>(null);
  const dragStart = useRef<{
    x: number;
    y: number;
    ox: number;
    oy: number;
  } | null>(null);

  const saveNickname = useStore((s) => s.saveNickname);

  const step = STEPS[idx];
  const rect = useTargetRect(step?.target);
  const last = idx === STEPS.length - 1;

  useEffect(() => {
    if (!open) {
      setIdx(0);
      setDragOffset({ x: 0, y: 0 });
      setNickname("");
    }
  }, [open]);

  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
  }, [idx]);

  useEffect(() => {
    function update() {
      setVp({ w: window.innerWidth, h: window.innerHeight });
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const measure = () => {
      const h = cardRef.current?.offsetHeight ?? CARD_H_FALLBACK;
      setCardH(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [idx, open]);

  function next() {
    // Se está no step do nome, salva antes de avançar
    if (step?.nameInput && nickname.trim()) {
      saveNickname(nickname.trim()).catch(() => {});
    }

    if (last) onComplete();
    else setIdx((i) => Math.min(i + 1, STEPS.length - 1));
  }

  function prev() {
    setIdx((i) => Math.max(i - 1, 0));
  }

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onSkip();
      if (e.key === "Enter" && !step?.nameInput) {
        if ((e.target as HTMLElement)?.closest("button")) return;
        e.preventDefault();
        next();
      }
      if (e.key === "ArrowRight") {
        if ((e.target as HTMLElement)?.tagName === "INPUT") return;
        e.preventDefault();
        next();
      }
      if (e.key === "ArrowLeft") {
        if ((e.target as HTMLElement)?.tagName === "INPUT") return;
        e.preventDefault();
        prev();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, last, nickname]);

  if (!open) return null;

  const padding = 12;
  const spotlight = rect
    ? {
        x: rect.left - padding,
        y: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  const auto = autoPlace(spotlight, vp, cardH);
  const targetLeft = clamp(auto.left + dragOffset.x, SAFE, vp.w - CARD_W - SAFE);
  const targetTop = clamp(
    auto.top + dragOffset.y,
    TOP_OVERHANG + SAFE,
    vp.h - cardH - SAFE,
  );

  function onDragPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("button, a, input, textarea")) return;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      ox: dragOffset.x,
      oy: dragOffset.y,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }
  function onDragPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    setDragOffset({
      x: dragStart.current.ox + (e.clientX - dragStart.current.x),
      y: dragStart.current.oy + (e.clientY - dragStart.current.y),
    });
  }
  function onDragPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragStart.current) return;
    dragStart.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        key="iago-tour"
        className="fixed inset-0 z-[60]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <svg className="absolute inset-0 h-full w-full" aria-hidden>
          <defs>
            <mask id="iago-tour-mask">
              <rect width="100%" height="100%" fill="white" />
              {spotlight && (
                <motion.rect
                  initial={false}
                  animate={spotlight}
                  transition={{ type: "spring", damping: 26, stiffness: 240 }}
                  rx={14}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(2,6,23,0.72)"
            mask="url(#iago-tour-mask)"
            style={{ backdropFilter: "blur(2px)" }}
          />
          {spotlight && (
            <motion.rect
              initial={false}
              animate={spotlight}
              transition={{ type: "spring", damping: 26, stiffness: 240 }}
              rx={14}
              fill="none"
              stroke="rgba(217,70,239,0.7)"
              strokeWidth={1.6}
              style={{ filter: "drop-shadow(0 0 12px rgba(217,70,239,0.45))" }}
            />
          )}
        </svg>

        <motion.div
          className="absolute"
          style={{ width: CARD_W }}
          initial={false}
          animate={{ top: targetTop, left: targetLeft }}
          transition={{ type: "spring", damping: 28, stiffness: 260 }}
        >
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 24, stiffness: 220 }}
            className="relative"
          >
            <div
              className="pointer-events-none absolute"
              style={{ top: -54, right: -18, zIndex: 2 }}
            >
              <IagoAvatar size={104} state={step.state || "idle"} projection />
            </div>

            <div
              ref={cardRef}
              onPointerDown={onDragPointerDown}
              onPointerMove={onDragPointerMove}
              onPointerUp={onDragPointerUp}
              onPointerCancel={onDragPointerUp}
              className="relative overflow-hidden rounded-2xl border border-fuchsia-400/25 bg-slate-900/85 p-6 pr-8 pt-7 shadow-[0_20px_60px_rgba(139,92,246,0.35)] cursor-grab active:cursor-grabbing select-none"
              style={{
                backdropFilter: "blur(14px) saturate(140%)",
                WebkitBackdropFilter: "blur(14px) saturate(140%)",
                touchAction: "none",
              }}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-fuchsia-400/60 to-transparent" />
              <div
                className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(217,70,239,0.18) 0%, rgba(139,92,246,0.08) 50%, rgba(14,165,233,0) 80%)",
                }}
              />

              <div
                className="absolute left-1/2 top-1.5 flex -translate-x-1/2 items-center gap-1 text-fuchsia-300/40"
                aria-hidden
              >
                <GripHorizontal className="h-3 w-3" />
              </div>

              <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.18em] text-fuchsia-200/80">
                IAGO · {idx + 1} de {STEPS.length}
              </div>
              <h3 className="mb-3 pr-16 text-lg font-semibold leading-tight text-violet-50">
                {step.title}
              </h3>
              <p className="mb-5 text-[13.5px] leading-relaxed text-slate-200/90">
                {renderInline(step.body)}
              </p>

              {/* Input de nome (só no step que pede) */}
              {step.nameInput && (
                <div className="mb-5">
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && nickname.trim()) {
                        e.preventDefault();
                        next();
                      }
                    }}
                    placeholder="Seu nome ou apelido"
                    maxLength={40}
                    autoFocus
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 transition focus:border-fuchsia-400/50 focus:bg-black/40 focus:outline-none focus:shadow-[0_0_0_3px_rgba(217,70,239,0.12)]"
                  />
                </div>
              )}

              <div className="mb-4 flex gap-1.5">
                {STEPS.map((_, i) => (
                  <span
                    key={i}
                    className={
                      i === idx
                        ? "h-1.5 flex-1 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-sky-500"
                        : i < idx
                          ? "h-1.5 flex-1 rounded-full bg-fuchsia-400/40"
                          : "h-1.5 flex-1 rounded-full bg-white/10"
                    }
                  />
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={onSkip}
                  className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs text-slate-400 transition hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                >
                  <X className="h-3.5 w-3.5" /> Pular
                </button>
                <div className="flex items-center gap-2">
                  {idx > 0 && (
                    <button
                      onClick={prev}
                      className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-slate-300 transition hover:border-white/20 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                    >
                      Voltar
                    </button>
                  )}
                  <button
                    onClick={next}
                    autoFocus={idx === 0 ? false : undefined}
                    className="flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-sky-500 px-4 py-2 text-xs font-semibold text-white shadow-[0_6px_24px_rgba(217,70,239,0.4)] transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-200 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                  >
                    {step.cta || (last ? "Concluir" : "Próximo")}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-semibold text-fuchsia-200">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}
